import React, { createRef } from 'react';
import { Container } from '@plone/components';
import config from '@plone/volto/registry';
import FormattedDate from '@plone/volto/components/theme/FormattedDate/FormattedDate';
import UniversalLink from '@plone/volto/components/manage/UniversalLink/UniversalLink';
import Icon from '@plone/volto/components/theme/Icon/Icon';
import withQuerystringResults from '@plone/volto/components/manage/Blocks/Listing/withQuerystringResults';
import type { Content } from '@plone/types';
import { Pagination } from 'semantic-ui-react';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  PaginationNavigation: {
    id: 'Paginação',
    defaultMessage: 'Paginação',
  },
});

const EditalItemRow = ({ item }: { item: Content }) => {
  const { settings } = config;
  const iconName = settings.contentIcons[item['@type']];
  const date =
    item.effective.indexOf('1969-') === -1
      ? new Date(item.effective)
      : new Date(item.modified);
  // Avoid downloading the file when clicking on the title
  const linkProps =
    item['@type'] === 'File' ? { href: item['@id'] } : { item: item };
  return (
    <tr key={item['@id']} className={'edital-item-row'}>
      <td className={'title'}>
        <Icon name={iconName} />
        <UniversalLink {...linkProps}>{item.title}</UniversalLink>
      </td>
      <td className={'description'}>
        <p>{item.description}</p>
      </td>
      <td className={'date'}>
        <FormattedDate date={date} includeTime={true} />
      </td>
    </tr>
  );
};

const EditalItems = withQuerystringResults((props) => {
  const {
    listingItems,
    batch_size,
    totalPages,
    onPaginationChange,
    currentPage,
    prevBatch,
    nextBatch,
    properties,
    total,
  } = props;
  const intl = useIntl();
  const listingRef = createRef();
  return (
    <Container className="items">
      {listingItems?.length > 0 ? (
        <>
          <table className="edital-items-table ui attached compact table">
            <thead className="edital-items-header">
              <tr>
                <th>Título</th>
                <th>Descrição</th>
                <th>Publicado em</th>
              </tr>
            </thead>
            <tbody ref={listingRef} className="edital-items-body">
              {listingItems.map((item: any) => (
                <EditalItemRow key={item['@id']} item={item} />
              ))}
            </tbody>
          </table>
          {totalPages > 1 && (
            <div className="pagination-wrapper">
              <Pagination
                aria-label={intl.formatMessage(messages.PaginationNavigation)}
                activePage={currentPage}
                totalPages={totalPages}
                className="desktop-pagination"
                onPageChange={(e, { activePage }) => {
                  listingRef.current.scrollIntoView({ behavior: 'smooth' });
                  onPaginationChange(e, { activePage });
                }}
                firstItem={null}
                lastItem={null}
                prevItem={{
                  content: (
                    <FormattedMessage
                      id="Página Anterior"
                      defaultMessage="Página Anterior"
                    />
                  ),
                  icon: false,
                  'aria-disabled': !prevBatch,
                  className: !prevBatch ? 'disabled' : null,
                }}
                nextItem={{
                  content: (
                    <FormattedMessage
                      id="Próxima Página"
                      defaultMessage="Próxima Página"
                    />
                  ),
                  icon: false,
                  'aria-disabled': !nextBatch,
                  className: !nextBatch ? 'disabled' : null,
                }}
              />
              <Pagination
                className="mobile-pagination"
                activePage={currentPage}
                totalPages={totalPages}
                boundaryRange={1}
                siblingRange={0}
                onPageChange={(e, { activePage }) => {
                  listingRef.current.scrollIntoView({ behavior: 'smooth' });
                  onPaginationChange(e, { activePage });
                }}
                firstItem={null}
                lastItem={null}
                prevItem={undefined}
                nextItem={undefined}
              />
              <div className="total">
                <FormattedMessage id="Result" defaultMessage="Result" />{' '}
                {(currentPage - 1) * batch_size + 1}-
                {(currentPage - 1) * batch_size + listingItems.length}{' '}
                <FormattedMessage id="of" defaultMessage="of" />{' '}
                {total || properties.items_total}
              </div>
            </div>
          )}
        </>
      ) : (
        <span> </span>
      )}
    </Container>
  );
});

export default EditalItems;
